import { types, flow, getEnv } from 'mobx-state-tree';
import _find from 'lodash-es/find';
import _filter from 'lodash-es/filter';
import _uniqBy from 'lodash-es/uniqBy';
import _orderBy from 'lodash-es/orderBy';
import { fixableRejectionCodes, paymentMethod } from '@ourbranch/lookups';
import { clean } from '@ourbranch/policy-utils';

import Notification from 'core/helpers/notifications';
import { getMerged } from 'customer/components/policy/merge-segments';

import {
  GET_DOCUMENTS_AND_APPLICATIONS,
  GET_UPLOAD_URL,
  REGENERATE_POLICY_DOCUMENTS
} from 'customer/components/documents/documents.queries';
import { SEND_APPLICATION_LINK } from 'customer/components/documents/components/application-url-form/application-url-form.queries';
import {
  GET_ACCOUNT_POLICIES,
  GET_PAYMENT_METHOD_DETAILS,
  GET_VERIFIED_STATUS,
  VERIFY_BANK_ACCOUNT
} from './policies.queries';
import { PolicyStore } from './policy-store';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import PolicyList from './models/policy-list';

export const PoliciesStore = types
  .model({
    loading: types.optional(types.boolean, false),
    list: types.array(PolicyList),
    loadingDocuments: types.optional(types.boolean, false),
    documents: types.maybeNull(types.frozen()),
    unsignedApplications: types.maybeNull(types.frozen()),
    unsignedBixConversions: types.maybeNull(types.frozen()),
    policy: types.maybe(PolicyStore),
    notifications: types.maybeNull(types.array(types.string))
  })
  .actions((self) => ({
    getAccountPolicies: flow(function* getAccountPolicies(id) {
      self.loading = true;
      self.loadingDocuments = true;
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_ACCOUNT_POLICIES,
          fetchPolicy: 'network-only',
          variables: {
            id
          }
        });
        self.account = data.account;
        self.list = data.account.policies || [];
        self.documents = data?.documents;
        self.setNotifications();
        self.loading = false;
        self.loadingDocuments = false;
      } catch (error) {
        self.loading = false;
        self.loadingDocuments = false;
      }
    }),
    getBankAccountToken: flow(function* getBankAccountToken(accountId, policyId) {
      const { client } = getEnv(self);
      const { data } = yield client.query({
        query: GET_PAYMENT_METHOD_DETAILS,
        fetchPolicy: 'no-cache',
        variables: {
          accountId,
          policyId
        }
      });
      // _find will grab the first element returned, which is the default payment method.
      return _find(data.paymentMethods.allPaymentMethods, (paymentMethod) => {
        return paymentMethod?.bankName?.length > 0;
      });
    }),

    generateStripeBankInfo: flow(function* generateStripeBankInfo(accountId, policyId, stripeCustomerId) {
      const bankAccount = yield self.getBankAccountToken(accountId, policyId);
      if (bankAccount) {
        const res = yield self.getVerifiedStatus(policyId, stripeCustomerId, bankAccount.id);
        if (res) {
          self.addStripeBankInfo(policyId, res.status, bankAccount.id, bankAccount.last4);
        }
      }
    }),

    getVerifiedStatus: flow(function* getVerifiedStatus(policyId, stripeCustomerId, stripeBankAccountId) {
      const { client } = getEnv(self);
      const { data } = yield client.query({
        query: GET_VERIFIED_STATUS,
        fetchPolicy: 'no-cache',
        variables: {
          policyId,
          stripeCustomerId,
          stripeBankAccountId
        }
      });
      return { status: data.getVerifiedStatus.status };
    }),
    verifyBankAccount: flow(function* verifyBankAccount(verificationDetails) {
      const { client } = getEnv(self);

      try {
        const { data } = yield client.query({
          query: VERIFY_BANK_ACCOUNT,
          fetchPolicy: 'no-cache',
          variables: {
            verificationDetails
          }
        });
        return { status: data.verifyMicrodeposits.status };
      } catch (error) {
        if (error?.graphQLErrors[0]?.message) {
          throw new Error(error.graphQLErrors[0].message);
        }
        throw error;
      }
    }),
    setVerifiedStatus(status, policyId) {
      const foundPolicy = self.list?.find(({ id }) => id === policyId);
      if (foundPolicy) {
        foundPolicy.stripeBankInfo.achStatus = status;
      }
    },
    addStripeBankInfo(policyId, achStatus, stripeBankAccountId, last4) {
      const policy = self.list?.find((e) => e.id === policyId);
      const newStripeAccountInfo = {
        achStatus,
        stripeBankAccountId,
        last4
      };
      policy.stripeBankInfo = newStripeAccountInfo;
    },
    getDocuments: flow(function* getDocuments(accountId) {
      const { client } = getEnv(self);
      self.loadingDocuments = true;
      try {
        const { data } = yield client.query({
          query: GET_DOCUMENTS_AND_APPLICATIONS,
          variables: { accountId }
        });
        self.documents = data.documents;
        self.unsignedApplications = data.unsignedApplications;
        self.setNotifications();
        self.loadingDocuments = false;
      } catch (e) {
        self.loadingDocuments = false;
        return [];
      }
    }),
    regeneratePolicyDocuments: flow(function* regeneratePolicyDocuments(policyId) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: REGENERATE_POLICY_DOCUMENTS,
        variables: {
          policyId
        }
      });
    }),
    getUploadUrl: flow(function* getUploadUrl(accountId, fileName, mimeType) {
      const { client } = getEnv(self);
      return yield client.query({
        query: GET_UPLOAD_URL,
        variables: {
          accountId,
          fileName,
          mimeType
        }
      });
    }),
    sendApplicationLink: flow(function* sendApplicationUrl(accountId, policyId) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: SEND_APPLICATION_LINK,
        variables: {
          accountId,
          policyId
        }
      });
    }),
    setNotifications: function setNotifications() {
      const notifications = [];
      const bixConversionDocsInfo = self.getUnsignedBixConversion(self?.policy?.policy?.id);

      const isCrossSellEligible =
        self.list.length === 1 &&
        self.list.some(
          ({ offer }) =>
            !offer?.quote?.offerings?.autoRejectCode ||
            fixableRejectionCodes.includes(offer?.quote?.offerings?.autoRejectCode) ||
            !offer?.quote?.offerings?.homeownersRejectCode ||
            fixableRejectionCodes.includes(offer?.quote?.offerings?.homeownersRejectCode)
        );
      if (!bixConversionDocsInfo?.signedDocUploadedInS3) {
        notifications.push(Notification.Policy.SignedConversionDocuments);
      }
      if (isCrossSellEligible) {
        notifications.push(Notification.Policy.CrossSellEligibility);
      }

      if (notifications.length) {
        self.notifications = notifications;
      } else {
        self.notifications = null;
      }
    }
  }))
  .views((self) => ({
    getAutoPolicyCoverage() {
      const autoPolicy = self.list
        ? self.list.find(
            (policy) =>
              policy.policyType === 'A' &&
              (getPolicyStatus(policy) === PolicyStatus.Active || getPolicyStatus(policy) === PolicyStatus.Future)
          )
        : undefined;

      if (autoPolicy) {
        const { segments } = autoPolicy;
        const mappedSegments = segments.slice().map(clean);

        if (mappedSegments.length > 1) {
          const mergedPolicyDetails = getMerged(mappedSegments, mappedSegments[segments.length - 1].segmentId);
          return mergedPolicyDetails?.autoCoverage || autoPolicy?.policyDetails?.autoCoverage;
        }
        return mappedSegments[0]?.autoCoverage || autoPolicy?.policyDetails?.autoCoverage;
      }

      return undefined;
    },

    getHomePolicyDetails() {
      const homePolicy = self.list
        ? self.list.find((policy) => policy.policyType === 'H' && getPolicyStatus(policy) === PolicyStatus.Active)
        : undefined;
      let mergedPolicyDetails;
      if (homePolicy) {
        const { segments } = homePolicy;
        const mappedSegments = segments.slice().map(clean);
        if (mappedSegments.length >= 1) {
          mergedPolicyDetails = getMerged(mappedSegments, mappedSegments[segments.length - 1].segmentId);
        }
      }
      return mergedPolicyDetails;
    },

    get achPolicies() {
      return self.list
        ? _uniqBy(
            _filter(self.list, (policy) => {
              return policy.paymentMethod === paymentMethod.ACH;
            }),
            'stripeBankInfo.stripeBankAccountId'
          )
        : [];
    },
    getStripeBankInfo(policyId) {
      return self.list?.find((e) => e.id === policyId)?.stripeBankInfo;
    },
    getUnsignedBixConversion(policyId) {
      return {
        signedDocUploadedInS3: !self.unsignedBixConversions?.find((policy) => policy.policyId === policyId),
        signedTimestamp: self.policy.bixConversionSignedTimestamp
      };
    },
    getRecentPolicy() {
      return _orderBy(self.list, (policy) => policy.versionHistory[0].updatedDateTime, ['desc'])[0];
    }
  }));
